import { useEffect, useMemo } from "react";
import { useSession } from "@/hooks/useSession";
import { useSkills } from "@/hooks/useSkills";
import { useAgentStore } from "@/stores/agentStore";
import type {
  AgentInfo,
  SkillInfo,
} from "@/components/message-input/HighlightOverlay";

const DEFAULT_AGENTS: AgentInfo[] = [
  {
    id: "bash",
    slug: "bash",
    name: "Bash",
    description: "Primary agent",
    category: "Core",
  },
];

/**
 * Who the composer's `@` and `/` pickers list on a task's screen: Bash, the
 * person's own agents, their skills. Moved out of task/layout.tsx verbatim so
 * the layout's box and the Dock's pill list the same roster. Agent data is the
 * store's (single source of truth, refreshed by socket events); this only hands
 * it the session token and asks for the first fetch.
 */
export function useComposerRoster(): {
  agents: AgentInfo[];
  skills: SkillInfo[];
} {
  const { session } = useSession();
  const agentSummaries = useAgentStore((s) => s.agents);
  const setAgentAuthToken = useAgentStore((s) => s.setAuthToken);
  const fetchAgents = useAgentStore((s) => s.fetchMyAgents);
  const agentsFetched = useAgentStore((s) => s._hasFetched);

  useEffect(() => {
    if (session?.access_token) {
      setAgentAuthToken(session.access_token);
      if (!agentsFetched) fetchAgents();
    }
  }, [session?.access_token, setAgentAuthToken, fetchAgents, agentsFetched]);
  const { skills: skillSummaries } = useSkills();

  const agents = useMemo(() => {
    const apiAgents: AgentInfo[] = agentSummaries.map((agent) => ({
      id: agent.slug,
      slug: agent.slug,
      name: agent.name,
      description: agent.description,
      category: "Specialist",
    }));
    const agentMap = new Map<string, AgentInfo>();
    DEFAULT_AGENTS.forEach((a) => agentMap.set(a.slug, a));
    apiAgents.forEach((a) => agentMap.set(a.slug, a));
    return Array.from(agentMap.values());
  }, [agentSummaries]);

  const skills = useMemo(
    () =>
      skillSummaries.map((skill) => ({
        id: skill.slug,
        slug: skill.slug,
        name: skill.name,
        description: skill.description,
        category: skill.category,
        icon: skill.icon,
        color: skill.color,
      })),
    [skillSummaries]
  );

  return { agents, skills };
}
